import {
  Badge,
  type BadgeProps,
  Calendar,
  type CalendarProps,
  ConfigProvider,
  DatePicker,
  Flex,
  List,
  Modal,
  Popover,
  Select,
  Skeleton,
  Space,
  theme,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import SyncSuspense from '../../../components/sync-suspense';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useQuery } from '../../../jsonapi-provider';
import { LocalDate } from '../../../utils/local-date';
import type Customer from '../../../entities/customer';
import type CycleMenu from '../../../entities/cycle-menu';

interface DataType {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  customers: Customer[];
}
export interface CycleMenusFiltersProps {
  onChange?: (filters: FiltersState) => void;
  initialFilters?: FiltersState;
}

interface FiltersState {
  curDate?: dayjs.Dayjs;
  sites?: number[];
}
interface CycleMenusPageState {
  filters?: CycleMenusFiltersProps['initialFilters'];
}
const reducer = (
  state: CycleMenusPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER';
    payload?: CycleMenusPageState['filters'];
  }
): CycleMenusPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload,
      };
  }
};
const serializeCycleMenuFilters = (filters: CycleMenusPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.curDate) {
    parts.push(
      `(endDate=ge=${new LocalDate(filters?.curDate)
        .startOf('month')
        .toISOString()},(repeatUntil=isnull=false;repeatUntil=ge=${new LocalDate(
        filters?.curDate.startOf('month')
      ).toISOString()}));startDate=le=${new LocalDate(
        filters?.curDate.endOf('month').add(1, 'M')
      ).toISOString()}`
    );
  }

  if (filters?.sites && filters?.sites.length > 0 && !filters.sites.includes(-1)) {
    parts.push(`sites.id=supersetof=(${filters?.sites.join(',')})`);
  }
  return parts.length ? parts.join(';') : undefined;
};

const CycleMenusCalendarModal = (props: DataType): ReactNode => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { isOpen, onConfirm, onCancel, customers } = props;
  const [state, setState] = useReducer(reducer, {
    filters: { curDate: dayjs().utc(true).startOf('d') },
  });

  const [debouncedState] = useDebouncedState({ state, time: 0 });
  const cycleMenus = useQuery([
    'cycle-menu',
    {
      filter: serializeCycleMenuFilters(debouncedState.filters),
      page: {
        number: 1,
        size: 10000,
      },
    },
  ]);
  useEffect(() => {
    cycleMenus.refetch?.();
  }, [debouncedState]);

  const dateCellRender = (value: Dayjs): ReactNode => {
    const listData =
      (
        cycleMenus.data?.filter(
          (cycleMenu: CycleMenu) =>
            (cycleMenu.repeatUntil ?? cycleMenu.endDate).isSameOrAfter(value) &&
            cycleMenu.startDate.isSameOrBefore(value)
        ) ?? []
      ).map((cm: CycleMenu) => ({
        type: cm.isEnabled ? 'success' : 'error',
        content: cm.name,
        id: cm.id,
      })) ?? [];
    return (
      <ConfigProvider renderEmpty={() => 'No cycle Menus'}>
        <SyncSuspense
          fallback={
            <>
              <Space direction="vertical">
                <Space>
                  <Skeleton.Avatar active size={'small'} shape={'circle'} />
                  <Skeleton.Button active size="small" shape={'round'} />
                </Space>
                <Space>
                  <Skeleton.Avatar active size={'small'} shape={'circle'} />
                  <Skeleton.Input active size="small" block={true} />
                </Space>
              </Space>
            </>
          }
          condition={!cycleMenus.isLoading}
        >
          <Popover
            content={
              <>
                <List
                  dataSource={listData}
                  split={false}
                  renderItem={(item: any) => (
                    <List.Item style={{ margin: 0, padding: 0 }}>
                      <Typography.Link
                        style={{ marginLeft: 10 }}
                        onClick={(e) => {
                          const cms = [];
                          cms.push(item.id);
                          if (item.parentCycleMenu) cms.push(item.parentCycleMenu.id);
                          navigate(`/cycle-menus/${item.id}`);
                          e.stopPropagation();
                        }}
                      >
                        <Badge
                          status={item.type as BadgeProps['status']}
                          style={{ color: token.colorPrimaryText }}
                          text={<Typography.Text ellipsis>{item.content}</Typography.Text>}
                        />
                      </Typography.Link>
                    </List.Item>
                  )}
                />
              </>
            }
            title="Cycle Menus"
            trigger="click"
            style={{ position: 'relative' }}
          >
            <List
              dataSource={listData.slice(0, 2)}
              split={false}
              renderItem={(item: any) => (
                <List.Item style={{ margin: 0, padding: 0 }}>
                  <Badge
                    style={{ color: token.colorPrimaryText }}
                    status={item.type as BadgeProps['status']}
                    text={
                      <Typography.Text ellipsis>
                        {item.content.slice(0, 14)}
                        {item.content.length > 13 && '...'}
                      </Typography.Text>
                    }
                  />
                </List.Item>
              )}
            />
            {listData.length > 2 && (
              <Typography.Link italic underline style={{ marginLeft: 10 }}>
                +{listData.length - 2} more
              </Typography.Link>
            )}
          </Popover>
        </SyncSuspense>
      </ConfigProvider>
    );
  };
  const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
    if (info.type === 'date') return dateCellRender(current);
    return info.originNode;
  };
  return (
    <>
      <Modal
        title={
          <Typography.Title
            style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }}
            level={3}
          >
            Cycle Menus
          </Typography.Title>
        }
        open={isOpen}
        onOk={onConfirm}
        onCancel={onCancel}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={1300}
        forceRender
      >
        <Calendar
          headerRender={() => (
            <Flex justify="space-between">
              <Space>
                <Typography.Text>Select Site</Typography.Text>
                <Select
                  showSearch
                  placeholder="Select a person"
                  optionFilterProp="label"
                  defaultValue={-1}
                  style={{ minWidth: 300 }}
                  onChange={(value) => {
                    setState({ type: 'SET_FILTER', payload: { ...state.filters, sites: [value] } });
                  }}
                  options={[
                    { label: 'All sites', value: -1 },
                    ...customers
                      .map((cust) =>
                        cust.sites.map((site) => ({ label: site.name, value: site.id }))
                      )
                      .flat(),
                  ]}
                />
              </Space>
              <Space>
                <Typography.Text>Select Month</Typography.Text>
                <DatePicker
                  picker="month"
                  defaultValue={state.filters?.curDate}
                  value={state.filters?.curDate}
                  onChange={(date) => {
                    setState({
                      type: 'SET_FILTER',
                      payload: { ...state.filters, curDate: date },
                    });
                  }}
                />
              </Space>
            </Flex>
          )}
          mode="month"
          cellRender={cellRender}
        />
      </Modal>
    </>
  );
};

export default CycleMenusCalendarModal;
